import { create } from "zustand";

/**
 * Modal Manager Store
 * 
 * Centralized management for ensuring only one popup/modal can be active at a time.
 * This manager coordinates across all modal types in the application.
 * 
 * Usage:
 * 1. Call `openModal(modalId)` to register and open a modal
 * 2. The manager will automatically close any currently open modal
 * 3. Use `isModalActive(modalId)` to check if your modal should render
 * 4. Call `closeModal()` when the modal is closed by user action
 */

export interface ModalManagerState {
  /** The currently active modal identifier */
  activeModalId: string | null;
  
  /** Timestamp when the current modal was opened (for debugging/analytics) */
  openedAt: number | null;
  
  /** Queue of callbacks to execute when a modal is closed */
  onCloseCallbacks: Map<string, () => void>;
  
  /**
   * Opens a modal with the given identifier.
   * Automatically closes any currently open modal first.
   * @param modalId - Unique identifier for the modal
   * @param onClose - Optional callback to execute when this modal is closed by another modal opening
   */
  openModal: (modalId: string, onClose?: () => void) => void;
  
  /**
   * Closes the currently active modal.
   * @param modalId - Optional: only close if this specific modal is active
   */
  closeModal: (modalId?: string) => void;
  
  /**
   * Checks if a specific modal is currently active.
   * @param modalId - The modal identifier to check
   */
  isModalActive: (modalId: string) => boolean;
  
  /**
   * Gets the currently active modal ID.
   */
  getActiveModalId: () => string | null;
  
  /**
   * Force closes all modals without triggering callbacks.
   * Use sparingly - mainly for cleanup/reset scenarios.
   */
  forceCloseAll: () => void;
  
  /**
   * Registers a close callback for a modal without opening it.
   * Useful when a modal manages its own open state but wants to participate
   * in the single-modal constraint.
   */
  registerCloseCallback: (modalId: string, callback: () => void) => void;
  
  /**
   * Unregisters a close callback for a modal.
   */
  unregisterCloseCallback: (modalId: string) => void;
}

const useModalManager = create<ModalManagerState>((set, get) => ({
  activeModalId: null,
  openedAt: null,
  onCloseCallbacks: new Map(),
  
  openModal: (modalId: string, onClose?: () => void) => {
    const state = get();
    
    // If the same modal is already open, do nothing to avoid infinite loops
    // The original callback is still valid and stored in onCloseCallbacks
    if (state.activeModalId === modalId) {
      return;
    }
    
    // Close any currently open modal first
    if (state.activeModalId) {
      const existingCallback = state.onCloseCallbacks.get(state.activeModalId);
      if (existingCallback) {
        // Execute callback asynchronously to avoid state update conflicts
        setTimeout(() => {
          existingCallback();
        }, 0);
      }
    }
    
    // Set up the new modal
    const newCallbacks = new Map(state.onCloseCallbacks);
    if (onClose) {
      newCallbacks.set(modalId, onClose);
    }
    
    set({
      activeModalId: modalId,
      openedAt: Date.now(),
      onCloseCallbacks: newCallbacks,
    });
  },
  
  closeModal: (modalId?: string) => {
    const state = get();
    
    // If modalId is provided, only close if it matches the active modal
    if (modalId && state.activeModalId !== modalId) {
      return;
    }
    
    // Clean up the callback for the closed modal
    if (state.activeModalId) {
      const newCallbacks = new Map(state.onCloseCallbacks);
      newCallbacks.delete(state.activeModalId);
      
      set({
        activeModalId: null,
        openedAt: null,
        onCloseCallbacks: newCallbacks,
      });
    }
  },
  
  isModalActive: (modalId: string) => {
    return get().activeModalId === modalId;
  },
  
  getActiveModalId: () => {
    return get().activeModalId;
  },
  
  forceCloseAll: () => {
    set({
      activeModalId: null,
      openedAt: null,
      onCloseCallbacks: new Map(),
    });
  },
  
  registerCloseCallback: (modalId: string, callback: () => void) => {
    const state = get();
    const newCallbacks = new Map(state.onCloseCallbacks);
    newCallbacks.set(modalId, callback);
    set({ onCloseCallbacks: newCallbacks });
  },
  
  unregisterCloseCallback: (modalId: string) => {
    const state = get();
    const newCallbacks = new Map(state.onCloseCallbacks);
    newCallbacks.delete(modalId);
    set({ onCloseCallbacks: newCallbacks });
  },
}));

export default useModalManager;

/**
 * Modal IDs for commonly used modals in the application.
 * Use these constants when calling openModal() to ensure consistency.
 */
export const MODAL_IDS = {
  // Drawer-based modals (from useDrawerStore)
  BOOKING_DETAILS: "drawer:bookingDetails",
  BOOKING_EDIT: "drawer:editBooking",
  BOOKING_NEW: "drawer:newBooking",
  BOOKING_TRANSFER: "drawer:transferBooking",
  BOOKING_CANCEL: "drawer:cancelConfirmation",
  CUSTOMER_DETAILS: "drawer:customerDetails",
  EVENT_DETAILS: "drawer:eventDetails",
  EVENT_NEW: "drawer:newEvent",
  EVENT_EDIT: "drawer:editEvent",
  EVENT_CANCEL: "drawer:cancelEvent",
  EVENT_DUPLICATE: "drawer:duplicateEvent",
  LESSON_ADULTS: "drawer:adults",
  LESSON_JUNIORS: "drawer:juniors",
  LESSON_CLUB_FITTING: "drawer:club_fitting",
  TRANSACTION: "drawer:transaction",
  EXPORT: "drawer:export",
  REFUND: "drawer:refund",
  COACH_PRICES: "drawer:coach-prices",
  COACH_ADD: "drawer:addNewCoach",
  COACH_EDIT: "drawer:editCoachProfile",
  COACH_DELETE: "drawer:deleteCoachModal",
  DAILY_SCHEDULE: "drawer:dailyBookings&Events",
  SPECIAL_DAY: "drawer:specialDayModal",
  
  // Popup store modals
  MARKETING_POPUP: "popup:marketing",
  
  // Feature popup modals
  RELEASING_NOTES: "popup:releasingNotes",
  
  // Inline modals
  INLINE_CUSTOMER: "inline:customer",
  
  // PIN verification
  PIN_VERIFICATION: "modal:pinVerification",
  
  // Confirmation dialogs
  DELETE_CONFIRMATION: "modal:deleteConfirmation",
  CANCEL_CONFIRMATION: "modal:cancelConfirmation",
  MOVE_CONFIRMATION: "modal:moveConfirmation",
  
  // Other common modals
  CROP_IMAGE: "modal:cropImage",
  MEDIA_LIBRARY: "modal:mediaLibrary",
  ENVELOPE_WARNING: "modal:envelopeWarning",
  EMBED_CODE: "modal:embedCode",
  TEMPLATE_SELECTOR: "modal:templateSelector",
  AI_FULLSCREEN: "modal:aiFullscreen",
  COMMAND_PALETTE: "modal:commandPalette",
} as const;

export type ModalId = (typeof MODAL_IDS)[keyof typeof MODAL_IDS] | string;